import { useCallback, useEffect, useRef } from 'react';

const TIMEOUT_DURATION = 300 * 600 * 1000; // 30 minutes in milliseconds

// Store original fetch globally to prevent multiple wrappings
let originalFetch = null;
let isFetchWrapped = false;
let resetTimerCallback = null;

/**
 * Custom hook to handle user inactivity timeout for HIPAA compliance.
 * Automatically logs out users after 30 minutes of inactivity.
 * 
 * Activity is detected through:
 * - API calls to /api/* endpoints (via global fetch wrapper)
 * - React Router navigation changes
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.isAuthenticated - Whether user is currently authenticated
 * @param {Function} options.onLogout - Callback function to execute on logout
 * @param {Object} options.location - Location object from react-router-dom's useLocation hook
 */
export function useInactivityTimer({ isAuthenticated, onLogout, location }) {
    const timerRef = useRef(null);
    const onLogoutRef = useRef(onLogout);

    // Keep onLogout ref up to date
    useEffect(() => {
        onLogoutRef.current = onLogout;
    }, [onLogout]);

    /**
     * Resets the inactivity timer to 30 minutes
     */
    const resetTimer = useCallback(() => {
        // Clear existing timer
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            console.log('Cleared existing timer');
        }

        // Only set timer if user is authenticated
        if (isAuthenticated) {
            timerRef.current = setTimeout(() => {
                console.log('Inactivity timeout: Logging out user after 30 minutes of inactivity');
                // Set flag to indicate inactivity logout
                sessionStorage.setItem('inactivityLogout', 'true');
                if (onLogoutRef.current) {
                    onLogoutRef.current();
                }
                timerRef.current = null;
            }, TIMEOUT_DURATION);
        }
    }, [isAuthenticated]);

    /**
     * Clears the inactivity timer
     */
    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            console.log('Cleared existing timer');
        }
    }, []);

    // Store resetTimer callback globally so fetch wrapper can access it
    useEffect(() => {
        resetTimerCallback = resetTimer;
    }, [resetTimer]);

    // Setup global fetch wrapper to detect API calls
    useEffect(() => {
        // Store original fetch if not already stored
        if (!originalFetch) {
            originalFetch = window.fetch;
        }

        // Only wrap fetch if not already wrapped
        if (!isFetchWrapped) {
            // Create wrapped fetch function
            const wrappedFetch = async function (...args) {
                const url = args[0];
                let urlString = null;

                // Extract URL string from different argument types
                if (typeof url === 'string') {
                    urlString = url;
                } else if (url instanceof Request) {
                    urlString = url.url;
                } else if (url && typeof url === 'object' && url.url) {
                    urlString = url.url;
                }

                // Check if this is an API call to /api/*
                // Handle relative paths (/api/...) and absolute URLs that contain /api/ as a path segment
                if (urlString) {
                    let isApiCall = false;

                    // Relative path starting with /api/
                    if (urlString.startsWith('/api/')) {
                        isApiCall = true;
                    }
                    // Absolute URL containing /api/ as a path segment (e.g., http://example.com/api/...)
                    else if (urlString.includes('://') && urlString.includes('/api/')) {
                        // Extract pathname from URL
                        try {
                            const urlObj = new URL(urlString);
                            if (urlObj.pathname.startsWith('/api/')) {
                                isApiCall = true;
                            }
                        } catch (e) {
                            // If URL parsing fails, fall back to simple string check
                            if (urlString.includes('/api/')) {
                                isApiCall = true;
                            }
                        }
                    }

                    if (isApiCall) {
                        // Reset inactivity timer on API call
                        if (resetTimerCallback) {
                            resetTimerCallback();
                        }
                    }
                }

                // Call original fetch
                return originalFetch.apply(this, args);
            };

            // Replace global fetch with wrapped version
            window.fetch = wrappedFetch;
            isFetchWrapped = true;
        }

        // Initial timer setup if authenticated
        if (isAuthenticated) {
            resetTimer();
        } else {
            clearTimer();
        }

        // Cleanup: restore original fetch only if this is the last instance
        // Note: In practice, since this hook is only used once in App.js,
        // we could restore on unmount, but we'll keep it wrapped for simplicity
        return () => {
            clearTimer();
        };
    }, [isAuthenticated, resetTimer, clearTimer]);

    // Reset timer on navigation (location.pathname changes)
    useEffect(() => {
        if (isAuthenticated && location) {
            resetTimer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.pathname, isAuthenticated, resetTimer]);

    // Clear timer when user logs out or becomes unauthenticated
    useEffect(() => {
        if (!isAuthenticated) {
            clearTimer();
        }
    }, [isAuthenticated, clearTimer]);
}

